/* Adelais mobile purchase UI. Native Broadcast remains the source of variant/cart state. */
(() => {
  if (customElements.get('adelais-mobile-purchase')) return;
  class AdelaisMobilePurchase extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.section = document.getElementById(this.dataset.section);
      this.form = document.getElementById(this.dataset.form);
      this.dialog = this.querySelector('dialog');
      if (!this.section || !this.form || !this.dialog?.showModal) return;
      try { this.product = JSON.parse(this.section.querySelector('[data-product-json]').textContent); } catch { return; }
      // Subscription and gift-card recipient forms need their complete native controls.
      if (this.product.selling_plan_groups?.length || this.product.gift_card) return;
      this.nativeButton = this.form.querySelector('[data-add-to-cart]');
      if (!this.nativeButton || this.form.querySelector('[type="file"]') || !document.querySelector('cart-drawer, [data-cart-page]')) return;
      this.media = matchMedia('(max-width: 749px)');
      this.qty = this.querySelector('[data-am-quantity]');
      this.confirm = this.querySelector('[data-am-confirm]');
      this.error = this.querySelector('[data-am-error]');
      const listen = (target, name, fn) => target.addEventListener(name, fn, {signal: this.abort.signal});
      listen(this, 'click', e => this.onClick(e));
      listen(this.qty, 'change', () => this.updateQuantity());
      listen(this.section, 'theme:variant:change', () => this.scheduleSync());
      listen(this.section, 'change', () => this.scheduleSync());
      listen(this.dialog, 'close', () => this.onClose());
      listen(this.dialog, 'click', e => {
        const rect = this.dialog.getBoundingClientRect();
        if (e.target === this.dialog && (e.clientY < rect.top || e.clientX < rect.left || e.clientX > rect.right || e.clientY > rect.bottom)) this.dialog.close();
      });
      listen(this.media, 'change', () => { if (!this.media.matches && this.dialog.open) this.dialog.close(); });
      listen(document, 'theme:product:add', e => {
        if (!this.pending || e.detail?.button !== this.nativeButton) return;

        const buy = this.pendingMode === 'buy';

        if (buy) {
          this.confirm.disabled = true;
          this.confirm.textContent = this.label(
            'Checkout',
            'Opening checkout…'
          );
          this.startCheckout();
          return;
        }

        this.pending = false;
        this.dialog.close();
      });
      listen(document, 'theme:product:add-error', e => {
        if (!this.pending || e.detail?.button !== this.nativeButton) return;
        this.pending = false;
        this.showError(e.detail.response?.description || this.label('CartError', 'This item could not be added. Please check your selection and try again.'));
        this.sync();
      });
      this.setupPickupAvailability();
      this.makeOptions();
      this.makeSiblings();
      this.sync();
      // Observe the stable block: Discount Ninja replaces the native price children.
      const nativePrice = this.section.querySelector('.product__content .product__price__wrap')?.closest('.product__block');
      if (nativePrice) {
        this.priceObserver = new MutationObserver(() => this.schedulePriceSync());
        this.priceObserver.observe(nativePrice, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'hidden']});
      }
      for (const event of ['la:dn:product:discount:calculated', 'limoniapps:discountninja:product:discountapplied', 'la:dn:cart:updated', 'limoniapps:discountninja:cart:updated']) {
        listen(document, event, () => this.schedulePriceSync());
      }
      listen(window, 'pageshow', () => this.syncPrice());
      this.hidden = false;
      this.section.classList.add('am-enhanced');
      document.documentElement.classList.add('am-mobile-bar');
      const bar = this.querySelector('.am-purchase-bar');
      if (bar) {
        this.barObserver = new ResizeObserver(() => {
          if (this.media.matches) document.documentElement.style.setProperty('--am-bar-height', `${bar.getBoundingClientRect().height}px`);
        });
        this.barObserver.observe(bar);
      }
      this.restoreColourSelection();
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.onClose();
      this.abort?.abort();
      this.syncQueued = false;
      this.priceObserver?.disconnect();
      cancelAnimationFrame(this.priceSyncFrame);
      this.priceSyncFrame = null;
      this.barObserver?.disconnect();
      document.documentElement.style.removeProperty('--am-bar-height');
      this.galleryObserver?.disconnect();
      if (this.pickup && this.originalPickupFetch) this.pickup.fetchPickupAvailability = this.originalPickupFetch;
      this.section?.querySelector('.am-price-stock')?.remove();
      this.galleryDots?.remove();
      this.section?.classList.remove('am-enhanced');
      document.documentElement.classList.remove('am-mobile-bar');
    }
    setupPickupAvailability() {
      const pickup = this.section.querySelector('pickup-availability');
      if (!pickup?.fetchPickupAvailability) return;
      this.pickup = pickup;
      this.originalPickupFetch = pickup.fetchPickupAvailability;
      const original = this.originalPickupFetch;
      const media = this.media;
      pickup.fetchPickupAvailability = function(event) {
        // Query Shopify's real store inventory even when online inventory is sold out.
        // Broadcast otherwise hides the complete pickup section before requesting it.
        const variant = event?.detail?.variant;
        if (media.matches && variant && !variant.available) {
          return original.call(this, {detail: {variant: {...variant, available: true}}});
        }
        return original.call(this, event);
      };
    }
    label(key, fallback) { return this.dataset['label' + key] || fallback; }
    nativeOptions(index) {
      return [...this.section.querySelectorAll('[data-single-option-selector]')].filter(el => el.dataset.index === `option${index + 1}`);
    }
    values() {
      return this.product.options.map((_, index) => {
        const inputs = this.nativeOptions(index);
        return inputs.find(el => el.type === 'radio' ? el.checked : true)?.value;
      });
    }
    variant() {
      const id = new FormData(this.form).get('id');
      return this.product.variants.find(v => String(v.id) === String(id));
    }
    makeOptions() {
      const host = this.querySelector('[data-am-options]');
      host.replaceChildren();
      if (this.product.variants.length === 1 && this.product.variants[0].title === 'Default Title') return;
      this.product.options.forEach((option, index) => {
        // Retain the native selected value, but omit a hidden stock selector from the popup.
        if (this.nativeOptions(index)[0]?.closest('[data-single-stock-option]')) return;
        const field = document.createElement('fieldset');
        const legend = document.createElement('legend');
        legend.textContent = typeof option === 'string' ? option : option.name;
        field.append(legend);
        const buttons = document.createElement('div'); buttons.className = 'am-options';
        [...new Set(this.product.variants.map(v => v.options[index]))].forEach(value => {
          const button = document.createElement('button'); button.type = 'button';
          button.textContent = value; button.dataset.amOption = index; button.dataset.value = value;
          button.setAttribute('aria-pressed', 'false'); buttons.append(button);
        });
        field.append(buttons);
        const sizeSource = this.nativeOptions(index)[0]?.closest('.selector-wrapper')?.querySelector('product-modal .product-tabs');
        if (sizeSource) {
          const guide = document.createElement('div');
          guide.className = 'am-size-guide'; guide.hidden = true;
          guide.id = `am-size-guide-${this.dataset.section}-${index}`;
          const toggle = document.createElement('button'); toggle.type = 'button';
          toggle.className = 'am-size-guide-toggle'; toggle.textContent = this.label('FindSize', 'Find my size');
          toggle.setAttribute('aria-expanded', 'false'); toggle.setAttribute('aria-controls', guide.id);
          legend.classList.add('am-size-legend'); legend.append(toggle);
          const close = document.createElement('button'); close.type = 'button';
          close.className = 'am-size-guide-back'; close.textContent = this.label('BackSize', 'Back to size selection');
          guide.append(close);
          const content = document.createElement('div'); content.className = 'rte';
          const panels = sizeSource.querySelectorAll('.tab-content');
          (panels.length ? [...panels] : [sizeSource]).forEach(panel => {
            const copy = panel.cloneNode(true);
            copy.removeAttribute('id'); copy.removeAttribute('hidden');
            copy.className = 'am-size-guide-content';
            copy.querySelectorAll('script,style').forEach(el => el.remove());
            copy.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
            copy.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; });
            content.append(copy);
          });
          guide.append(content); field.append(guide);
          const setOpen = open => {
            guide.hidden = !open;
            toggle.setAttribute('aria-expanded', String(open));
            if (open) requestAnimationFrame(() => {
              if (!this.dialog.open || guide.hidden) return;
              const body = this.querySelector('.am-dialog-body');
              const top = body.scrollTop + guide.getBoundingClientRect().top - body.getBoundingClientRect().top - 8;
              body.scrollTo({top:Math.max(0, top), behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
            });
          };
          toggle.addEventListener('click', () => setOpen(guide.hidden), {signal:this.abort.signal});
          close.addEventListener('click', () => { setOpen(false); toggle.focus(); }, {signal:this.abort.signal});
        }
        host.append(field);
      });
    }
    makeSiblings() {
      const original = this.section.querySelector('.product__siblings');
      if (!original) return;
      const host = this.querySelector('[data-am-siblings]');
      host.replaceChildren();
      const field = document.createElement('fieldset');
      const legend = document.createElement('legend'); legend.textContent = this.label('Color', 'Color'); field.append(legend);
      const choices = document.createElement('div'); choices.className = 'am-options am-colours';
      original.querySelectorAll('a.sibling__link').forEach(link => {
        const a = document.createElement('a'); a.href = link.href;
        const swatch = link.closest('[data-tooltip]');
        a.textContent = swatch?.dataset.tooltip || link.getAttribute('aria-label') || link.textContent.trim();
        const img = link.querySelector('img');
        if (img) { const image = document.createElement('img'); image.src = img.currentSrc || img.src; image.alt = ''; a.prepend(image); }
        if (link.classList.contains('sibling__link--current')) a.setAttribute('aria-current', 'page');
        choices.append(a);
      });
      if (choices.children.length) { field.append(choices); host.append(field); }
    }
    openPanel(mode, opener) {
      if (!this.media.matches || this.dialog.open) return;
      this.mode = mode === 'buy' ? 'buy' : 'add'; this.opener = opener;
      this.error.hidden = true; this.sync();
      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.locked = true;
      this.dialog.showModal();
    }
    restoreColourSelection() {
      let saved;
      try {
        saved = JSON.parse(sessionStorage.getItem('adelais-mobile-colour') || 'null');
        if (!saved || saved.path !== location.pathname) return;
        sessionStorage.removeItem('adelais-mobile-colour');
      } catch { return; }
      if (Date.now() - saved.time > 120000 || !this.media.matches) return;
      // The sibling product owns its variant IDs; transfer option values, never the old ID.
      const names = this.product.options.map(option => typeof option === 'string' ? option : option.name);
      const match = this.product.variants.find(variant => variant.options.every((value, i) => value === saved.options?.[names[i]]));
      if (match) match.options.forEach((value, index) => {
        const input = this.nativeOptions(index).find(el => el.type === 'radio' ? el.value === value : true);
        if (input) {
          if (input.type === 'radio') input.checked = true; else input.value = value;
          input.dispatchEvent(new Event('change', {bubbles:true}));
        }
      });
      if (Number.isSafeInteger(saved.quantity) && saved.quantity > 0) {
        const native = this.form.querySelector('[name="quantity"]');
        this.qty.value = Math.min(Number(native?.max) || Infinity, Math.max(Number(native?.min) || 1, saved.quantity));
        this.updateQuantity();
      }
      this.openPanel(saved.mode, this.querySelector(saved.mode === 'buy' ? '[data-am-open="buy"]' : '[data-am-open="add"]'));
    }
    onClick(e) {
      const cartLink = e.target.closest('[data-am-cart]');
      if (cartLink) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          e.preventDefault();
          drawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show', {bubbles: true}));
        }
        return;
      }
      const opener = e.target.closest('[data-am-open]');
      if (opener) {
        this.openPanel(opener.dataset.amOpen, opener); return;
      }
      const colour = e.target.closest('[data-am-siblings] a');
      if (colour && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
        const target = new URL(colour.href, location.href);
        if (target.origin === location.origin) {
          if (target.pathname === location.pathname) { e.preventDefault(); return; }
          const options = Object.fromEntries(this.product.options.map((option, index) => [typeof option === 'string' ? option : option.name, this.values()[index]]));
          try {
            sessionStorage.setItem('adelais-mobile-colour', JSON.stringify({path:target.pathname, time:Date.now(), mode:this.mode, options, quantity:Number(this.qty.value)}));
          } catch { /* Navigation still works if browser storage is unavailable. */ }
        }
        return;
      }
      if (e.target.closest('[data-am-close]')) { this.dialog.close(); return; }
      const option = e.target.closest('[data-am-option]');
      if (option) {
        const inputs = this.nativeOptions(Number(option.dataset.amOption));
        const input = inputs.find(el => el.type === 'radio' ? el.value === option.dataset.value : true);
        if (input) {
          if (input.type === 'radio') input.checked = true; else input.value = option.dataset.value;
          input.dispatchEvent(new Event('change', {bubbles: true}));
        }
        this.error.hidden = true; this.scheduleSync(); return;
      }
      if (e.target.closest('[data-am-minus], [data-am-plus]')) {
        this.qty.value = Math.max(Number(this.qty.min) || 1, Number(this.qty.value) + (e.target.closest('[data-am-plus]') ? 1 : -1));
        this.updateQuantity(); return;
      }
      if (e.target.closest('[data-am-confirm]')) this.submit();
    }
    updateQuantity() {
      if (!this.qty.reportValidity()) return;
      const input = this.form.querySelector('[name="quantity"]');
      if (input) {
        input.value = this.qty.value;
        const popout = input.closest('popout-select');
        const label = popout?.querySelector('[data-popout-toggle-text]');
        if (label) label.textContent = this.qty.value;
        popout?.querySelectorAll('[data-popout-option]').forEach(option => {
          const current = option.dataset.value === this.qty.value;
          option.closest('li')?.classList.toggle('is-active', current);
          if (current) option.setAttribute('aria-current', 'true'); else option.removeAttribute('aria-current');
        });
        input.dispatchEvent(new Event('change', {bubbles:true}));
      }
    }
        async submitBuyNow() {
      const root = window.Shopify?.routes?.root || '/';
      const cartAddUrl =
        window.theme?.routes?.cart_add_url ||
        root + 'cart/add.js';

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          body: new FormData(this.form),
          signal: this.abort.signal
        });

        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
            result.message ||
            this.label(
              'CartError',
              'This item could not be added. Please try again.'
            )
          );
        }

        /*
         * The item is now in Shopify's cart.
         * Continue through Discount Ninja without rebuilding the cart drawer.
         */
        this.startCheckout();
      } catch (error) {
        if (error.name === 'AbortError') return;

        this.pending = false;
        this.showError(
          error.message ||
          this.label(
            'CartError',
            'This item could not be added. Please try again.'
          )
        );
        this.sync();
      }
    }

    startCheckout() {
      const root = window.Shopify?.routes?.root || '/';
      const checkoutUrl = root + 'checkout';

      if (this.hasAttribute('data-require-cart')) {
        window.location.assign(root + 'cart');
        return;
      }

      if (!window.discountNinja) {
        window.location.assign(checkoutUrl);
        return;
      }

      /*
       * Give Discount Ninja time to apply promotions and start checkout.
       * If the app does not respond, prevent Buy Now from remaining stuck.
       */
      const startingUrl = window.location.href;
      const fallbackTimer = window.setTimeout(() => {
        if (
          window.location.href === startingUrl &&
          document.visibilityState === 'visible'
        ) {
          window.location.assign(checkoutUrl);
        }
      }, 8000);

      window.addEventListener(
        'pagehide',
        () => window.clearTimeout(fallbackTimer),
        { once: true }
      );

      const eventName = window.discountNinja.api
        ? 'la:dn:checkout:initiate'
        : 'limoniapps:discountninja:checkout:initiate';

      document.dispatchEvent(new CustomEvent(eventName));
    }
    schedulePriceSync() {
      if (this.priceSyncFrame != null) return;
      this.priceSyncFrame = requestAnimationFrame(() => {
        this.priceSyncFrame = null;
        if (this.isConnected) this.syncPrice();
      });
    }
    syncPrice() {
      // A direct variant update supersedes an already queued price refresh.
      cancelAnimationFrame(this.priceSyncFrame);
      this.priceSyncFrame = null;
      const block = this.section.querySelector('.product__content .product__price__wrap')?.closest('.product__block');
      // Read the app's formatted current price, excluding its crossed-out original price.
      // No percentage is hardcoded: eligibility and formatting remain owned by Discount Ninja.
      const discounted = [...(block?.querySelectorAll('.limoniapps-discountninja-shortcode-discounted_price') || [])]
        .find(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      const native = this.section.querySelector('.product__content [data-product-price]');
      const price = (discounted || native)?.textContent.trim() || '';

      const visible = el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden';
      const original = discounted
        ? [...(block?.querySelectorAll('.limoniapps-discountninja-shortcode-original_price') || [])].find(visible)
        : [...(block?.querySelectorAll('[data-compare-price]') || [])].find(visible);
      const originalPrice = original?.textContent.trim() || '';
      const onSale = Boolean(originalPrice && originalPrice !== price);
      for (const target of this.querySelectorAll('[data-am-price], [data-am-bar-price]')) {
        const signature = JSON.stringify([price, originalPrice, onSale]);
        this.priceSignatures ||= new WeakMap();
        if (this.priceSignatures.get(target) === signature) continue;
        target.classList.toggle('am-price-pair', onSale);
        const parts = [];
        if (onSale) {
          const was = document.createElement('s');
          was.className = 'am-price-original';
          was.textContent = originalPrice;
          parts.push(was);
        }
        const current = document.createElement('span');
        current.className = onSale ? 'am-price-current' : '';
        current.textContent = price;
        parts.push(current);
        target.replaceChildren(...parts);
        this.priceSignatures.set(target, signature);
      }
    }
    scheduleSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      const signal = this.abort.signal;
      queueMicrotask(() => {
        if (signal.aborted) return;
        if (!this.syncQueued) return;
        this.syncQueued = false;
        if (this.isConnected) this.sync();
      });
    }
    sync() {
      this.syncQueued = false;
      const selected = this.values();
      this.querySelectorAll('[data-am-option]').forEach(button => {
        const index = Number(button.dataset.amOption);
        button.setAttribute('aria-pressed', String(selected[index] === button.dataset.value));
        const available = this.product.variants.some(v => v.available && v.options[index] === button.dataset.value && v.options.every((value, i) => i >= index || value === selected[i]));
        button.classList.toggle('am-unavailable', !available);
        // Keep options selectable so shoppers can recover from unavailable combinations.
        button.title = available ? '' : 'Unavailable with the current selection';
      });
      const variant = this.variant();
      let stockLabel = this.section.querySelector('.am-price-stock');
      if (!stockLabel) {
        stockLabel = document.createElement('span');
        stockLabel.className = 'am-price-stock';
        stockLabel.setAttribute('role', 'status');
        this.section.querySelector('.product__price__wrap')?.append(stockLabel);
      }
      stockLabel.textContent = !variant ? this.label('Unavailable', 'Unavailable') : !variant.available ? this.label('PriceSold', '- (Sold Out)') : '';
      stockLabel.hidden = this.dataset.showStockLabel === 'false' || Boolean(variant?.available);
      const displayedPrice = this.section.querySelector('[data-product-price]');
      if (displayedPrice) stockLabel.style.fontSize = getComputedStyle(displayedPrice).fontSize;
      this.querySelector('[data-am-stock]').textContent = !variant ? this.label('Choose', 'Choose an available combination') : variant.available ? this.label('Available', 'Available') : this.label('Sold', 'Sold out');
      this.confirm.disabled = this.pending || !variant?.available;
      this.confirm.textContent = this.pending ? this.label('Adding', 'Adding…') : !variant ? this.label('Unavailable', 'Unavailable') : !variant.available ? this.label('Sold', 'Sold Out') : this.mode === 'buy' ? (this.hasAttribute('data-require-cart') ? this.label('ContinueCart', 'Continue to Cart') : this.label('Buy', 'Buy Now')) : (this.section.querySelector('[data-product-preorder]') ? this.label('Preorder', 'Pre-order') : this.label('Add', 'Add to Cart'));
      const img = this.querySelector('[data-am-image]');
      if (img && variant?.featured_image?.src) img.src = variant.featured_image.src;
      const quantity = this.form.querySelector('[name="quantity"]');
      if (quantity && document.activeElement !== this.qty) this.qty.value = quantity.value;
      this.syncPrice();
    }
    submit() {
      if (
        this.pending ||
        !this.variant()?.available ||
        !this.qty.reportValidity()
      ) return;

      this.updateQuantity();

      if (this.nativeButton.disabled) {
        this.showError(
          this.label(
            'OptionsError',
            'Please complete the product options on the page.'
          )
        );
        return;
      }

      /*
       * Required line-item properties remain inside the native form.
       * Stop and show native validation if any required field is incomplete.
       */
      if (!this.form.checkValidity()) {
        this.dialog.close();
        this.form.reportValidity();
        return;
      }

      const directBuyNow =
        this.mode === 'buy' &&
        !this.hasAttribute('data-require-cart');

      this.pendingMode = this.mode;
      this.pending = true;
      this.error.hidden = true;
      this.sync();

      if (directBuyNow) {
        this.confirm.textContent = this.label(
          'Checkout',
          'Opening checkout…'
        );
        this.submitBuyNow();
        return;
      }

      /*
       * Normal Add to Cart and stores requiring cart terms continue using
       * Broadcast's original cart system.
       */
      this.nativeButton.click();
    }
    showError(message) { this.error.textContent = message; this.error.hidden = false; }
    onClose() {
      if (this.locked) { document.documentElement.style.overflow = this.savedOverflow; this.locked = false; }
      this.opener?.focus({preventScroll:true});
    }
  }
  customElements.define('adelais-mobile-purchase', AdelaisMobilePurchase);
})();
